import { LoadingOverlay, Text, useMantineTheme } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useCallback, useState } from 'react';
import { type FileWithPath } from '@mantine/dropzone';
import { useParams } from 'react-router-dom';

import { logAnalyticsEvent } from '../../utils/analyticsUtils';
import ModalContent from '../ModalContent';
import { handleFileUploadError } from '../../utils/apiUtils';
import useEnsisMutation from '../../hooks/useEnsisMutation';
import InitializeProposalForm, { type InitializeProposalFormValues } from '../Forms/InitializeProposalForm';
import ProposalProcessingMessage from '../ProposalProcessingMessage';
import useEnsisQuery from '../../hooks/useEnsisQuery';

interface Props {
  onClose: () => void
  opportunityUid?: string
}

const PAGE_LIMIT_ERROR_MESSAGE = 'Limit is 50 pages total. For longer RFPs, use Full Setup instead';

const FastDraftSetup: React.FC<Props> = (props: Props) => {
  const { onClose, opportunityUid } = props;
  const { other: colorVars } = useMantineTheme();
  const { organization } = useParams();
  const { data: opportunityData } = useEnsisQuery(`/app/opportunities/${opportunityUid}/data`, {
    enabled: opportunityUid !== undefined
  });

  const [fastDraftError, setFastDraftError] = useState('');

  const validateFiles = useCallback((files: FileWithPath[]) => {
    if (files === null || files.length === 0) {
      return 'Please select file(s) to upload';
    }
    return null;
  }, []);

  const form = useForm({
    initialValues: {
      files: [] as FileWithPath[],
      proposalName: '',
      additionalContext: ''
    },
    validate: {
      files: opportunityUid === undefined ? validateFiles : undefined,
      proposalName: (value: string) => (value.trim().length < 1 ? 'Proposal name is required' : null)
    },
    onValuesChange: ({ files }) => {
      if (files.length === 0) {
        setFastDraftError('');
      }
    }
  });

  const createProposalMutation = useEnsisMutation(
    '/app/proposals',
    {
      requestType: 'post',
      contentType: 'multipart/form-data',
      showSuccessMessage: false,
      queryKeysToInvalidate: ['/app/proposals', '/app/opportunity-assessments', '/app/proposals/recently-edited-by-me'],
      showFailureMessage: false,
      onError: (error) => {
        if (error?.response?.status === 413) {
          setFastDraftError(PAGE_LIMIT_ERROR_MESSAGE);
        } else {
          handleFileUploadError(error);
        }
      }
    }
  );

  const handleSubmitForm = useCallback((values: InitializeProposalFormValues) => {
    logAnalyticsEvent('CreateFastDraft');
    setFastDraftError('');
    createProposalMutation.mutate({
      name: values.proposalName,
      files: values.files as unknown as string[],
      additional_context: values.additionalContext,
      organization_slug: organization ?? '',
      generate_fast_draft: true,
      // Because of a quirk with multipart/form-data, we need to actually
      // exclude opportunity_uid from the request body if we want it to be null
      ...(
        opportunityUid !== undefined
          ? { opportunity_uid: opportunityUid }
          : {}
      )
    });
  }, [createProposalMutation.mutate, organization]);

  if (createProposalMutation.isSuccess) {
    return <ModalContent
      onClose={onClose}
      title='Generating Your Fast Draft'
      subtitle={`Your fast draft proposal is on its way! We're generating your content now and will email 
      you as soon as it's ready.`}
    >
      <ProposalProcessingMessage onClick={onClose} mb={40} />
    </ModalContent>;
  }

  const errorText = fastDraftError !== ''
    ? (
      <Text c='red' fz='sm'>
        {fastDraftError}
      </Text>
      )
    : undefined;

  const subtitleText = (
    <Text span fz='sm' c={colorVars.secondaryTextColor}>
      {'Max 50 pages across '}
      <Text span fz='sm' fw='bold' c={colorVars.secondaryTextColor}>
        all
      </Text>
      {' files'}
    </Text>
  );

  return (
    <ModalContent
      title={'Let\'s Get Started'}
      subtitle='First give your proposal a name, then import an opportunity'
      secondaryButton={{
        label: 'Cancel',
        onClick: onClose
      }}
      primaryButton={{
        label: 'Next',
        onClick: form.onSubmit(handleSubmitForm),
        disabled: createProposalMutation.isPending || !form.isValid()
      }}
      leftComponent={errorText}
      onClose={onClose}
    >
      <LoadingOverlay visible={createProposalMutation.isPending} zIndex={1000} />
      <InitializeProposalForm
        form={form}
        textProps={{ fw: 500 }}
        skipFileUpload={opportunityUid !== undefined}
        uploadedOpportunityFileNames={opportunityData?.files?.map((file) => file.file_name)}
        dropzoneProps={{ subtitleText }}
      />
    </ModalContent>
  );
};

export default FastDraftSetup;
